import { client } from '@/services/api/axios.ts';
import { useQuery } from '@tanstack/react-query';
import type { RangeGranularity } from '@/utils/granularity.ts';

type PlantEnergyManagementBaseRecord = {
  Battery_Charge: number;
  Battery_Discharge: number;
  Consumed: number;
  Consumed_from_Grid: number;
  Fed_to_Grid: number;
  Yield: number;
  time: string;
};

type PlantEnergyManagement15MinRecord = PlantEnergyManagementBaseRecord & {
  ROPEX_DAM_Price: number;
};

export type GetPlantEnergyManagementChartResponse<
  G extends RangeGranularity = RangeGranularity,
> = G extends '15min' ? PlantEnergyManagement15MinRecord[] : PlantEnergyManagementBaseRecord[];

export const useGetPlantEnergyManagementChartQuery = <
  G extends RangeGranularity = RangeGranularity,
>({
  plantId,
  dateRange,
  enabled = true,
}: {
  plantId: string | undefined;
  dateRange: {
    startDate: Date;
    endDate: Date | undefined;
    rangeGranularity: G;
  };
  enabled?: boolean;
}) => {
  return useQuery({
    queryKey: [
      'plants',
      plantId,
      'charts',
      dateRange.rangeGranularity,
      dateRange.startDate,
      dateRange.endDate,
    ],
    queryFn: async () => {
      const { data } = await client.get<{
        body: GetPlantEnergyManagementChartResponse<G>;
      }>(`/plants/${plantId}/charts`, {
        params: {
          granularity: dateRange.rangeGranularity,
          startDate: `${new Date(dateRange.startDate).getTime()}`,
          endDate: `${dateRange.endDate && new Date(dateRange.endDate).getTime()}`,
        },
      });

      return data.body;
    },
    retry: 1,
    staleTime: getRefetchInterval(dateRange.rangeGranularity),
    refetchInterval: getRefetchInterval(dateRange.rangeGranularity),
    enabled,
  });
};

const getRefetchInterval = (rangeGranularity: RangeGranularity) => {
  if (!rangeGranularity) return undefined;

  if (rangeGranularity === '1min') return 1000 * 60;
  if (rangeGranularity === '5min') return 1000 * 60 * 5;
  if (rangeGranularity === '15min') return 1000 * 60 * 15;

  if (rangeGranularity === 'hourly') return 1000 * 60 * 5;
  if (rangeGranularity === 'monthly') return 1000 * 60 * 5;
  if (rangeGranularity === 'daily') return 1000 * 60 * 5;
};
